import ModalBox from 'components/Common/ModalBox'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from 'components/Common/Button'
import { getTimeZoneDisplayRepresentation } from 'utils/dates'
import { momentLocaleFor } from 'i18n/formatDate'
import { UpdateMyProfileInput } from '__generated__/api-types-and-hooks'

export interface ITimezoneModalProps {
  setShowTimezoneModal: (state: boolean) => void
  onClose: () => void
  updateUserProfileAction: (payload: Partial<UpdateMyProfileInput>) => {
    type: 'UPDATE_USER_PROFILE_STARTED'
    payload: Partial<UpdateMyProfileInput>
  }
  defaultTimezone: string
}

const TimezoneModal = ({
  setShowTimezoneModal,
  updateUserProfileAction,
  defaultTimezone,
  onClose,
}: ITimezoneModalProps) => {
  const { t, i18n } = useTranslation()
  const [currentTime, setCurrentTime] = useState(new Date())
  const timezone = moment.tz.guess()
  // 'LT' instead of a hardcoded 'hh:mm a' so the clock follows the user's language
  // (en: "10:45 AM" · fr/vi: "10:45").
  const locale = momentLocaleFor(i18n.language)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 10000)

    return () => {
      clearInterval(interval)
    }
  }, [])
  const formattedTime = timezone
    ? moment(currentTime).tz(timezone).locale(locale).format('LT')
    : moment().locale(locale).format('LT')

  const formattedTimeInDefaultTimezone = defaultTimezone
    ? moment(currentTime).tz(defaultTimezone).locale(locale).format('LT')
    : moment().locale(locale).format('LT')

  const handleSubmit = () => {
    updateUserProfileAction({
      timezone,
    })
    setShowTimezoneModal(false)
  }

  return (
    <ModalBox
      onClose={onClose}
      title={t('settings.timezoneModal.title')}
      classes="rounded-[10px]"
      height={'min-h-[32rem]'}
    >
      <div className="flex flex-col  h-full w-full">
        <div className="mb-12 sm:mb-12 ml-7">
          <p className="leading-7 text-lg lg:text-xl mt-5 mb-2">
            {t('settings.timezoneModal.defaultZone')}
          </p>
          <div className="mb-10">
            <span className="inline-block font-bold text-lg text-black-light font-futura mr-2">
              {getTimeZoneDisplayRepresentation(defaultTimezone)}
            </span>
            <span className="inline-block font-bold text-lg text-black-light font-futura">
              ({formattedTimeInDefaultTimezone})
            </span>
          </div>
          <p className="leading-7 text-lg lg:text-xl mt-5 mb-2">
            {t('settings.timezoneModal.currentZone')}
          </p>
          <div className="mb-10">
            <span className="inline-block font-bold text-lg text-black-light font-futura mr-2">
              {getTimeZoneDisplayRepresentation(timezone)}
            </span>
            <span className="inline-block font-bold text-lg text-black-light font-futura">
              ({formattedTime})
            </span>
          </div>
          <p className="leading-7 text-lg lg:text-xl">{t('settings.timezoneModal.question')}</p>
        </div>

        <div className="h-full flex flex-row justify-end items-end p-7 border-t border-primary-outline">
          <Button
            className="!px-3 bg-white smd:w-48 smd:text-xl"
            label={t('settings.timezoneModal.noThanks')}
            type="button"
            variant="secondary"
            onClick={onClose}
          />
          <Button
            className="!px-3 ml-[0.94rem] smd:w-64 smd:text-xl"
            label={t('settings.timezoneModal.update')}
            type="button"
            variant="primary"
            onClick={handleSubmit}
          />
        </div>
      </div>
    </ModalBox>
  )
}

export default TimezoneModal
